import { useState } from 'react';

import { isFinlayStaffEmail } from '@/modules/auth/utils/isFinlayStaff';
import type { JobApi } from '@/modules/jobs/types';
import { CandidateNoticeSetting } from '@/modules/legal/components/candidate-notice-setting/CandidateNoticeSetting';
import { useUpdateJobAiDisclosureMutation } from '@/modules/legal/mutations/useUpdateJobAiDisclosureMutation';
import { useCandidateNoticesQuery } from '@/modules/legal/queries/useCandidateNoticesQuery';
import { useLegalDocumentsQuery } from '@/modules/legal/queries/useLegalDocumentsQuery';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

interface JobAiDisclosureSectionProps {
  job: JobApi;
}

/**
 * Finlay staff's per-job AI disclosure switch on an Agency job. A Finlay
 * recruiter takes part in the job's outreach, so it is not fully automated;
 * switching off binds Finlay, not the client, to the Agency Off Warning. It
 * can only take the disclosure away: when the company switched it off, the
 * switch is locked. Clients never see it.
 */
export function JobAiDisclosureSection({ job }: JobAiDisclosureSectionProps) {
  const currentUserQuery = useCurrentUserQuery();
  // The API also requires a Finlay email: client users can hold staff roles too.
  const isShown =
    job.operatingMode === 'Agency' &&
    isFinlayStaffEmail(currentUserQuery.data?.email);
  const noticesQuery = useCandidateNoticesQuery(
    isShown ? job.company.id : undefined,
  );
  const documentsQuery = useLegalDocumentsQuery();
  const updateMutation = useUpdateJobAiDisclosureMutation();
  const [isOffRecorded, setIsOffRecorded] = useState(false);

  if (!isShown) return null;

  const isCompanyOff = noticesQuery.data?.aiDisclosureEnabled === false;
  const offWarning =
    documentsQuery.data?.find(
      (document) => document.type === 'AgencyAiDisclosureOffWarning',
    )?.text ?? undefined;

  const change = (enabled: boolean) => {
    updateMutation.mutate(
      { jobId: job.id, enabled },
      // The API records nothing when the job already had this value.
      {
        onSuccess: (data) =>
          setIsOffRecorded(!enabled && data?.recorded === true),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI disclosure</CardTitle>
        <CardDescription>
          Internal only. The client never sees this setting.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {noticesQuery.isPending || documentsQuery.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : noticesQuery.isError || documentsQuery.isError ? (
          <InlineError>
            We could not load this setting. Reload the page to try again.
          </InlineError>
        ) : (
          <CandidateNoticeSetting
            description={
              isCompanyOff
                ? 'Switched off by the company, so no message for this job carries it.'
                : 'The first message to each candidate for this job says it was sent by an AI assistant.'
            }
            disabled={isCompanyOff}
            enabled={job.isAiDisclosureEnabled && !isCompanyOff}
            error={
              updateMutation.isError
                ? actionErrorMessage('save this setting', updateMutation.error)
                : null
            }
            isOffRecorded={isOffRecorded}
            isSaving={updateMutation.isPending}
            offWarning={offWarning}
            onChange={change}
            title="AI disclosure for this job"
          />
        )}
      </CardContent>
    </Card>
  );
}
